import React, { useState, useEffect, useRef } from 'react';

const VirtualGolfCourse = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [isPhaseTransitioning, setIsPhaseTransitioning] = useState(false);

  // Refs for each phase section
  const mainSectionRef = useRef(null);
  const phase1Ref = useRef(null);
  const phase2Ref = useRef(null);
  const phase3Ref = useRef(null);
  const phase4Ref = useRef(null);
  const phase5Ref = useRef(null);

  // Content data for phases
  const contentData = [
    {
      title: "We're venture builders and early-stage investors powering founders.",
      subtitle: "Venture building"
    },
    {
      title: "Corporate services to form, govern, and scale modern companies.",
      subtitle: "Corporate services"
    },
    {
      title: "Accounting that keeps you compliant, accurate, and investor-ready.",
      subtitle: "Accounting"
    },
    {
      title: "HR solutions that attract talent and elevate performance.",
      subtitle: "Human resources"
    },
    {
      title: "",
      subtitle: "Venturecorp"
    }
  ];

  // Calculate progress based on current phase (0 to 1)
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const maxPhase = Math.max(0, contentData.length - 1);
  const safePhase = clamp(currentPhase, 0, maxPhase);
  const targetProgress = maxPhase > 0 ? safePhase / maxPhase : 0;
  const [animatedProgress, setAnimatedProgress] = React.useState(targetProgress);
  const animationRef = React.useRef(null);
  const animationStartRef = React.useRef(null);
  const startProgressRef = React.useRef(targetProgress);

  // Smoothly animate progress whenever the phase changes
  React.useEffect(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationStartRef.current = null;
    startProgressRef.current = animatedProgress;

    const durationMs = 700; // smoother duration
    const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

    const step = (timestamp) => {
      if (animationStartRef.current == null) {
        animationStartRef.current = timestamp;
      }
      const elapsed = timestamp - animationStartRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const easedT = easeInOut(t);
      const next = startProgressRef.current + (targetProgress - startProgressRef.current) * easedT;
      setAnimatedProgress(next);
      if (t < 1) {
        animationRef.current = requestAnimationFrame(step);
      }
    };

    animationRef.current = requestAnimationFrame(step);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [targetProgress]);

  const scrollProgress = animatedProgress;

  // Content phases based on current phase
  const contentPhase = safePhase;
  const currentItem = contentData[contentPhase] || { title: '', subtitle: '' };

  // Center-based scroll spy for consistent phase detection
  useEffect(() => {
    const phaseRefs = [phase1Ref, phase2Ref, phase3Ref, phase4Ref, phase5Ref];
    let rafId = 0;

    const updatePhaseFromScroll = () => {
      rafId = 0;
      const viewportCenterY = window.innerHeight / 2;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      for (let i = 0; i < phaseRefs.length; i += 1) {
        const el = phaseRefs[i].current;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const distance = Math.abs(sectionCenter - viewportCenterY);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }

      // Only update when it actually changes to avoid flicker
      setCurrentPhase(prev => (prev !== closestIndex ? closestIndex : prev));
    };

    const onScroll = () => {
      if (rafId) return; // throttle to one per frame
      rafId = window.requestAnimationFrame(updatePhaseFromScroll);
    };

    // Initial computation
    updatePhaseFromScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div id="virtual-golf" className="relative">
      {/* Fixed content section - similar to sustainability page */}
      <div className="relative">
        {/* Fixed-position content display */}
        <div className="sticky top-0 h-screen bg-black overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
            {/* Animated balls with easing - all converge to final dot position */}
            {/* Top left ball */}
            <div
              className="absolute w-4 h-4 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-400 + (Math.pow(scrollProgress, 1.5) * 400)}px, ${-300 + (Math.pow(scrollProgress, 1.5) * 300)}px) scale(${0.8 + (scrollProgress * 0.4)})`,
                opacity: currentPhase === 4 ? 1 : 0.7 + (scrollProgress * 0.3),
                filter: `blur(${(1 - scrollProgress) * 2}px)`
              }}
            />

            {/* Top right ball */}
            <div
              className="absolute w-5 h-5 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${400 - (Math.pow(scrollProgress, 1.3) * 400)}px, ${-250 + (Math.pow(scrollProgress, 1.3) * 250)}px) scale(${0.7 + (scrollProgress * 0.5)})`,
                opacity: currentPhase === 4 ? 1 : 0.8 + (scrollProgress * 0.2),
                filter: `blur(${(1 - scrollProgress) * 1.5}px)`
              }}
            />

            {/* Bottom left ball */}
            <div
              className="absolute w-3 h-3 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-350 + (Math.pow(scrollProgress, 1.4) * 350)}px, ${400 - (Math.pow(scrollProgress, 1.4) * 400)}px) scale(${0.9 + (scrollProgress * 0.3)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 1.8}px)`
              }}
            />

            {/* Bottom right ball */}
            <div
              className="absolute w-6 h-6 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${450 - (Math.pow(scrollProgress, 1.2) * 450)}px, ${350 - (Math.pow(scrollProgress, 1.2) * 350)}px) scale(${0.6 + (scrollProgress * 0.6)})`,
                opacity: currentPhase === 4 ? 1 : 0.7 + (scrollProgress * 0.3),
                filter: `blur(${(1 - scrollProgress) * 2.2}px)`
              }}
            />

            {/* Center left ball */}
            <div
              className="absolute w-4 h-4 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-500 + (Math.pow(scrollProgress, 1.6) * 500)}px, ${-50 + (Math.pow(scrollProgress, 1.6) * 50)}px) scale(${0.8 + (scrollProgress * 0.4)})`,
                opacity: currentPhase === 4 ? 1 : 0.5 + (scrollProgress * 0.5),
                filter: `blur(${(1 - scrollProgress) * 2.5}px)`
              }}
            />

            {/* Center right ball */}
            <div
              className="absolute w-5 h-5 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${500 - (Math.pow(scrollProgress, 1.4) * 500)}px, ${50 - (Math.pow(scrollProgress, 1.4) * 50)}px) scale(${0.7 + (scrollProgress * 0.5)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 2}px)`
              }}
            />

            {/* Far top ball */}
            <div
              className="absolute w-3 h-3 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-200 + (Math.pow(scrollProgress, 1.8) * 200)}px, ${-400 + (Math.pow(scrollProgress, 1.8) * 400)}px) scale(${0.9 + (scrollProgress * 0.3)})`,
                opacity: currentPhase === 4 ? 1 : 0.4 + (scrollProgress * 0.6),
                filter: `blur(${(1 - scrollProgress) * 3}px)`
              }}
            />

            {/* Far bottom ball */}
            <div
              className="absolute w-4 h-4 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${300 - (Math.pow(scrollProgress, 1.3) * 300)}px, ${450 - (Math.pow(scrollProgress, 1.3) * 450)}px) scale(${0.8 + (scrollProgress * 0.4)})`,
                opacity: currentPhase === 4 ? 1 : 0.5 + (scrollProgress * 0.5),
                filter: `blur(${(1 - scrollProgress) * 2.3}px)`
              }}
            />

            {/* Additional scattered balls */}
            <div
              className="absolute w-2 h-2 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-600 + (Math.pow(scrollProgress, 2) * 600)}px, ${100 - (Math.pow(scrollProgress, 2) * 100)}px) scale(${1 + (scrollProgress * 0.2)})`,
                opacity: currentPhase === 4 ? 1 : 0.3 + (scrollProgress * 0.7),
                filter: `blur(${(1 - scrollProgress) * 3.5}px)`
              }}
            />

            <div
              className="absolute w-3 h-3 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${550 - (Math.pow(scrollProgress, 1.7) * 550)}px, ${-150 + (Math.pow(scrollProgress, 1.7) * 150)}px) scale(${0.9 + (scrollProgress * 0.3)})`,
                opacity: currentPhase === 4 ? 1 : 0.4 + (scrollProgress * 0.6),
                filter: `blur(${(1 - scrollProgress) * 2.8}px)`
              }}
            />

            <div
              className="absolute w-5 h-5 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-300 + (Math.pow(scrollProgress, 1.5) * 300)}px, ${500 - (Math.pow(scrollProgress, 1.5) * 500)}px) scale(${0.7 + (scrollProgress * 0.5)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 2.1}px)`
              }}
            />

            <div
              className="absolute w-4 h-4 bg-white rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${600 - (Math.pow(scrollProgress, 1.4) * 600)}px, ${200 - (Math.pow(scrollProgress, 1.4) * 200)}px) scale(${0.8 + (scrollProgress * 0.4)})`,
                opacity: currentPhase === 4 ? 1 : 0.5 + (scrollProgress * 0.5),
                filter: `blur(${(1 - scrollProgress) * 2.4}px)`
              }}
            />

            {/* Purple balls for visual variety */}
            {/* Purple ball 1 */}
            <div
              className="absolute w-3 h-3 bg-purple-400 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-320 + (Math.pow(scrollProgress, 1.3) * 320)}px, ${-180 + (Math.pow(scrollProgress, 1.3) * 180)}px) scale(${0.7 + (scrollProgress * 0.5)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 2.0}px)`
              }}
            />

            {/* Purple ball 2 */}
            <div
              className="absolute w-5 h-5 bg-purple-500 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${380 - (Math.pow(scrollProgress, 1.6) * 380)}px, ${280 - (Math.pow(scrollProgress, 1.6) * 280)}px) scale(${0.6 + (scrollProgress * 0.6)})`,
                opacity: currentPhase === 4 ? 1 : 0.7 + (scrollProgress * 0.3),
                filter: `blur(${(1 - scrollProgress) * 1.8}px)`
              }}
            />

            {/* Purple ball 3 */}
            <div
              className="absolute w-2 h-2 bg-purple-300 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-480 + (Math.pow(scrollProgress, 1.8) * 480)}px, ${350 - (Math.pow(scrollProgress, 1.8) * 350)}px) scale(${0.9 + (scrollProgress * 0.3)})`,
                opacity: currentPhase === 4 ? 1 : 0.5 + (scrollProgress * 0.5),
                filter: `blur(${(1 - scrollProgress) * 2.5}px)`
              }}
            />

            {/* Purple ball 4 */}
            <div
              className="absolute w-4 h-4 bg-purple-600 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${520 - (Math.pow(scrollProgress, 1.2) * 520)}px, ${-220 + (Math.pow(scrollProgress, 1.2) * 220)}px) scale(${0.8 + (scrollProgress * 0.4)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 2.2}px)`
              }}
            />

            {/* Purple ball 5 */}
            <div
              className="absolute w-3 h-3 bg-purple-400 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${-150 + (Math.pow(scrollProgress, 1.7) * 150)}px, ${-380 + (Math.pow(scrollProgress, 1.7) * 380)}px) scale(${0.7 + (scrollProgress * 0.5)})`,
                opacity: currentPhase === 4 ? 1 : 0.6 + (scrollProgress * 0.4),
                filter: `blur(${(1 - scrollProgress) * 2.1}px)`
              }}
            />

            {/* Purple ball 6 */}
            <div
              className="absolute w-2 h-2 bg-purple-500 rounded-full shadow-lg transition-all duration-700 ease-out"
              style={{
                transform: currentPhase === 4
                  ? `translate(${window.innerWidth < 768 ? 120 : window.innerWidth < 1024 ? 150 : 200}px, ${25}px) scale(0.5)`
                  : `translate(${280 - (Math.pow(scrollProgress, 1.5) * 280)}px, ${420 - (Math.pow(scrollProgress, 1.5) * 420)}px) scale(${0.9 + (scrollProgress * 0.3)})`,
                opacity: currentPhase === 4 ? 1 : 0.5 + (scrollProgress * 0.5),
                filter: `blur(${(1 - scrollProgress) * 2.3}px)`
              }}
            />

            {currentPhase === 4 && (
              <div
                className="absolute left-1/2 top-1/2 flex items-center justify-center transition-all duration-500 ease-out"
                style={{
                  transform: `translate(-50%, -50%)`,
                  opacity: 1
                }}
              >
                {/* Final text - all balls converge to form the final dot after the text */}
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-AfacadFluxLight uppercase font-bold text-white">
                  <span>Venturecorp</span>
                </h2>
              </div>
            )}

            {/* Central content */}
            <div
              className="text-center text-white max-w-4xl px-8 transition-all duration-200 ease-out"
              style={{
                opacity: isPhaseTransitioning ? 0.7 : 1,
                transform: `scale(${isPhaseTransitioning ? 0.99 : 1}) translateY(${isPhaseTransitioning ? 3 : 0}px)`
              }}
            >
              {currentPhase < 4 && (
          <div className="mb-6">
                  <span
                    className="text-sm font-semibold tracking-widest uppercase transition-all duration-500"
                    style={{
                      color: `hsl(${280 + (currentPhase * 20)}, 70%, 70%)`
                    }}
                  >
                    {currentItem.subtitle}
                  </span>
          </div>
              )}
              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight transition-all duration-500"
              >
                {currentItem.title}
              </h1>

              {/* Progress dots - hide in final phase since V and ball are in animation area */}
              {currentPhase < 4 && (
                <div className="flex justify-center space-x-2 mt-8">
                  {contentData.map((_, index) => (
                    <div
                      key={index}
                      className="w-2 h-2 rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: currentPhase >= index ? '#ffffff' : '#666666',
                        transform: `scale(${currentPhase === index ? 1.4 : currentPhase > index ? 1.1 : 1})`
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white text-center">
            <div className="w-32 h-1 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-200 ease-out"
                style={{ width: `${scrollProgress * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Invisible scroll sections that trigger content changes */}
        <div className="relative z-0">
          <div ref={phase1Ref} className="h-screen snap-start"></div>
          <div ref={phase2Ref} className="h-screen snap-start"></div>
          <div ref={phase3Ref} className="h-screen snap-start"></div>
          <div ref={phase4Ref} className="h-screen snap-start"></div>
          <div ref={phase5Ref} className="h-screen snap-start"></div>
        </div>
      </div>
    </div>
  );
};

export default VirtualGolfCourse; 